import { useCallback, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { getHistogramBarIntervalForRange } from '@antimatterhq/ui/components/histogram';
import { getQueryTabStatus } from './useQueryStatus';
import {
  queryTabAtomFamily,
  updateTabAtomFamily,
} from '@/atoms/query/queryAtoms';
import { queryStartValueAtomFamily } from '@/atoms/query/queryResultsAtoms';
import { clearQueryTableStateAtomFamily } from '@/atoms/query/queryTableResultsAtoms';
import { useCreateQuery } from '@/hooks/dbui/useCreateQuery';
import { convertToInterval } from '@/hooks/components/query/useQueryHistogram';

export const useRunQuery = (tabId: string) => {
  const { mutate: createQuery, isPending: isCreatingQuery } = useCreateQuery();
  const tab = useAtomValue(queryTabAtomFamily(tabId));
  const [error, setError] = useState<string | undefined>(undefined);

  // Run the query
  const runQuery = useAtomCallback(
    useCallback(
      (get, set) => {
        setError(undefined);
        const currentTab = get(queryTabAtomFamily(tabId));
        if (!currentTab) {
          setError('No tab found');
          return;
        }

        if (!currentTab.sql) {
          setError('SQL is required to run a query');
          return;
        }

        if (!currentTab.dateRange) {
          setError('Date range is required to run a query');
          return;
        }

        set(updateTabAtomFamily(tabId), {
          status: 'pending',
          queryId: undefined,
          queriedDataRange: undefined,
          columnDefinitions: undefined,
          histogram: {
            timeRanges: [],
            interval: '',
          },
        });

        // Clear the query start value atom and table results
        set(queryStartValueAtomFamily(tabId), null);
        set(clearQueryTableStateAtomFamily(tabId));

        createQuery(
          {
            sql: currentTab.sql,
            dateRange: {
              startDate: currentTab.dateRange.start.toAbsoluteString(),
              endDate: currentTab.dateRange.end.toAbsoluteString(),
            },
          },
          {
            onSuccess: (data) => {
              // Calculate histogram interval based on the date range
              const interval = getHistogramBarIntervalForRange(
                currentTab.dateRange.start,
                currentTab.dateRange.end,
              );

              set(updateTabAtomFamily(tabId), {
                queryId: data.id,
                status: getQueryTabStatus(data),
                queriedDataRange: {
                  start: currentTab.dateRange.start.copy(),
                  end: currentTab.dateRange.end.copy(),
                },
                // Initialize histogram time ranges immediately
                histogram: {
                  timeRanges: [
                    {
                      start: currentTab.dateRange.start.copy(),
                      end: currentTab.dateRange.end.copy(),
                    },
                  ],
                  interval: convertToInterval(interval),
                },
              });
            },
            onError: (createError: Error) => {
              setError(createError.message);
            },
          },
        );
      },
      [tabId, createQuery],
    ),
  );

  // Consolidate the various states into a single pending state
  const isPending =
    isCreatingQuery ||
    (!!tab?.queryId &&
      (tab?.status === 'pending' || tab?.status === 'running'));

  return {
    isPending,
    runQuery,
    error,
  };
};
